
import * as React from 'react';
import { IRenderProps, renderChildrenWithProps, StatePropsUpdater } from '../../utilities/components/RenderProps';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemCacheLinkFacet } from '../decorators/connectToItemCache';
import { IFaceted } from '../../models/Facet';
import { mapObject } from '../../utilities/objects';
import { IRelayReceiverStateProps, createRelay, IRelayReceiverProps } from '../../utilities/components/Relay';
import { createMemoizer } from '../../utilities/Memoizer';
import { graft, MirrorPatch, IncrementPatch } from '../../utilities/Graft';
import { BackgroundTaskContextProvider } from '../backgroundTaskRunner/BackgroundTaskHost';

const withActionSetRelayPropsToStatePropsMemoizer = createMemoizer((
  values: IRelayReceiverStateProps<IActionStateProps>['values']
): IWithActionSetStateProps<string> => {
  return {
    actions: values
  };
});

const EMPTY_ACTION_PROPS: IActionBaseProps = {};

/**
 * @internal
 */
const ACTION_RELAY = createRelay<IActionBaseProps>(EMPTY_ACTION_PROPS, {
  propsFilter: {
    isAvailable: true,
    isToggled: true,
    onExecute: true
  }
});

/**
 * @public
 */
export interface IOnExecuteProps {
  event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | MouseEvent;
}

/**
 * @public
 */
export interface IActionBaseProps {
  /**
   * Whether or not the action should be available.
   * Default is `true`.
   */
  isAvailable?: boolean | undefined;
  /**
   * Whether or not the action should be in a toggled state.
   * Default is `undefined`, which means it does not support toggling.
   */
  isToggled?: boolean | undefined;
  /**
   * A callback which invoked to implement the action's execution behavior.
   * This function must always return a Promise.
   */
  onExecute?: (onExecuteProps: IOnExecuteProps) => Promise<void>;
}

const IS_AVAILABLE: keyof IActionBaseProps = 'isAvailable';

/**
 * @public
 */
export const ActionBase: React.ComponentType<IActionBaseProps> = ACTION_RELAY.Sender;

/**
 * @public
 */
export type IActionInput = React.ComponentType<{}> | JSX.Element | null;

/**
 * @internal
 */
export function renderAction(actionInput: IActionInput | undefined): JSX.Element | null {
  const ActionInput = actionInput;

  return typeof ActionInput === 'function' ? (
    <ActionInput />
  ) : ActionInput || null;
}

/**
 * @internal
 */
export interface ISelectedActionProps {
  action?: IActionInput;
}

/**
 * @internal
 */
export class ActionSelector extends React.PureComponent<ISelectedActionProps> {
  public render(): JSX.Element | null {
    return renderAction(this.props.action);
  }
}

/**
 * @internal
 */
export function evaluateAction(itemCache: IItemCache, actionInput?: IActionInput): JSX.Element | null {
  const element = renderAction(actionInput);

  if (!element) {
    return null;
  }

  if (typeof element.type === 'function') {
    const connectToItemCacheLink = connectToItemCacheLinkFacet.unpack(element.type as {} as IFaceted);

    if (connectToItemCacheLink) {
      const Component = connectToItemCacheLink.component;

      const componentProps = connectToItemCacheLink.mapItemCacheToProps(itemCache, {
        ...element.props
      });

      if (Component === ActionSelector as {} as React.ComponentType<{}>) {
        const action = (componentProps as {} as ISelectedActionProps).action;

        return evaluateAction(itemCache, action);
      }

      return (
        <Component
          { ...element.props }
          { ...componentProps }
        />
      );
    }
  }

  return element;
}

/**
 * The state of the action passed as a render prop.
 * @public
 */
export interface IActionStateProps {
  /**
   * Whether or not the action is currently available.
   */
  isAvailable: boolean;
  /**
   * Whether or not the action is in a toggled state. Undefined means it does not support toggle behavior.
   */
  isToggled?: boolean;
  /**
   * Number of current active executons of the action.
   */
  executingCount: number;
  /**
   * Executes the action and waits for it to resolve.
   */
  execute: (props?: IOnExecuteProps) => Promise<void>;
}

/**
 * @internal
 */
export const INITIAL_ACTION_STATE: IActionStateProps = {
  isAvailable: false,
  executingCount: 0,
  execute: defaultExecute
};

interface IActionPropsByKey {
  [key: string]: IActionBaseProps | undefined;
}

interface IActionExecutionTrackerProps extends IRenderProps<IRelayReceiverStateProps<IActionStateProps>> {
  actions: IRelayReceiverStateProps<IActionBaseProps>['values'];
}

interface IActionExecutionTrackerState {
  actions: {
    [actionKey: string]: IActionStateProps;
  };
}

class ActionExecutionTracker extends React.PureComponent<IActionExecutionTrackerProps, IActionExecutionTrackerState> {
  private _getMemoizedWrapper: (actions: IActionExecutionTrackerState['actions']) => IRelayReceiverStateProps<IActionStateProps>;

  private _isDisposed?: boolean;

  constructor(props: IActionExecutionTrackerProps) {
    super(props);

    this.state = {
      actions: this._mapActionStates(props.actions)
    };

    this._getMemoizedWrapper = createMemoizer((
      actions: IActionExecutionTrackerState['actions']
    ): IRelayReceiverStateProps<IActionStateProps> => {
      return {
        values: actions
      };
    });
  }

  public componentWillReceiveProps(nextProps: IActionExecutionTrackerProps): void {
    const {
      actions: previousStateProps
    } = this.props;

    const {
      actions
    } = nextProps;

    if (actions !== previousStateProps) {
      this.setState((previousState: IActionExecutionTrackerState) => {
        const nextActions = this._mapActionStates(actions, previousStateProps, previousState);

        return graft(previousState, {
          actions: new MirrorPatch(nextActions)
        });
      });
    }
  }

  public render(): JSX.Element | null {
    const stateProps = this._getMemoizedWrapper(this.state.actions);

    return renderChildrenWithProps(this.props.children, stateProps);
  }

  public componentWillUnmount(): void {
    this._isDisposed = true;
  }

  private _mapActionStates(
    stateProps: IActionExecutionTrackerProps['actions'],
    previousStateProps?: IActionExecutionTrackerProps['actions'],
    previousState?: IActionExecutionTrackerState
  ): IActionExecutionTrackerState['actions'] {
    const previousActions = previousState && previousState.actions;

    return mapObject(stateProps, (
      actionState: IActionBaseProps | undefined = EMPTY_ACTION_PROPS,
      actionKey: string | number = ''
    ): IActionStateProps => {
      const previousActionProps = previousStateProps && previousStateProps[actionKey];

      const previousOverrides = previousActions && previousActions[actionKey];

      const execute = !previousOverrides || !previousActionProps || actionState.onExecute !== previousActionProps.onExecute ?
        this._wrapExecute(actionKey as string, actionState.onExecute) :
        previousOverrides.execute;

      const executingCount = !previousOverrides ? 0 : previousOverrides.executingCount;

      const hasAvailable = IS_AVAILABLE in actionState;

      const {
        isAvailable = !hasAvailable && !!actionState.onExecute,
        isToggled
      } = actionState;

      return {
        isToggled,
        isAvailable,
        execute,
        executingCount
      };
    });
  }

  private _wrapExecute(key: string, onExecute?: (props: IOnExecuteProps) => Promise<void>): (props?: IOnExecuteProps) => Promise<void> {
    if (!onExecute) {
      return defaultExecute;
    }

    return async (props: IOnExecuteProps = {}): Promise<void> => {
      this.setState(increment(key, 1));

      try {
        await onExecute(props);
      } finally {
        if (!this._isDisposed) {
          this.setState(increment(key, -1));
        }
      }
    };
  }
}

const memoizedJoiner = createMemoizer((baseProps: IActionPropsByKey): (overrideProps: IActionPropsByKey) => IActionPropsByKey => {
  return createMemoizer((overrideProps: IActionPropsByKey) => {
    return {
      ...baseProps,
      ...overrideProps
    };
  });
});

function withActionStateTracker(
  actionProps: IActionPropsByKey,
  renderProp: IRenderProps<IRelayReceiverStateProps<IActionStateProps>>['children']
): IRenderProps<IRelayReceiverStateProps<IActionBaseProps>>['children'] {
  return (relayProps: IRelayReceiverStateProps<IActionBaseProps>) => {
    return (
      <ActionExecutionTracker actions={ memoizedJoiner(relayProps.values)(actionProps) }>{ renderProp }</ActionExecutionTracker>
    );
  };
}

/**
 * @public
 */
export interface IWithActionSetStateProps<K extends string = string> {
  /**
   * A mapping of provided keys for actions and their current states.
   * These keys should match those passed into props.
   */
  actions: {
    [P in K]: IActionStateProps | undefined;
  };
}

/**
 * @public
 */
export interface IWithActionSetProps<K extends string = string> extends IRenderProps<IWithActionSetStateProps<K>> {
  /**
   * A mapping of provided keys for actions to their rendered instances.
   * The keys will be used when reporting the status, either via the 'children' render prop or
   * via onActionsUpdated.
   *
   * @example
   * ```tsx
   * <WithActionSet
   *  actions={
   *      {
   *          'a': <UploadAction />
   *          'b': <ItemShareAction itemKey={ itemKey } />
   *      }
   *  }
   * />
   * ```
   */
  actions: {
    [P in K]: IActionInput;
  };
  /**
   * A callback invoked whenever any action updates, which provides the current states of all actions.
   * Use this callback to map action state into the state of a host component.
   */
  onActionsUpdated?: (actionSetProps: IWithActionSetStateProps<K>) => void;
}

/**
 * Component which evaluates the states of multiple Action instances simultaneously and
 * provides hooks to get their availability and execution callback.
 * @public
 */
export class WithActionSet<K extends string = string> extends React.PureComponent<IWithActionSetProps<K>> {
  public render(): JSX.Element {
    const {
      actions,
      children,
      onActionsUpdated
    } = this.props;

    return renderActionRelay(
      actions,
      withActionSetRelayPropsToStatePropsMemoizer,
      children,
      onActionsUpdated);
  }
}

/**
 * The props for the WithAction render prop component.
 * @public
 */
export interface IWithActionProps<K extends string = ''> extends IRenderProps<IActionStateProps> {
  action?: IActionInput;
  onActionUpdated?: (actionProps: IActionStateProps) => void;
  actionKey?: K;
}

/**
 * Component which passes the state of a chosen action to a render prop.
 * @public
 */
export class WithAction<K extends string = ''> extends React.PureComponent<IWithActionProps<K>> {
  public render(): JSX.Element {
    const {
      action,
      children,
      actionKey = '',
      onActionUpdated
    } = this.props;

    return renderActionRelay(
      {
        [actionKey]: action || null
      },
      (values: IRelayReceiverStateProps<IActionStateProps>['values']) => {
        const {
          [actionKey]: actionState
        } = values;

        return actionState || INITIAL_ACTION_STATE;
      },
      children,
      onActionUpdated);
  }
}

function renderActionRelay<TStateProps>(
  actions: {
    [key: string]: IActionInput;
  },
  getStateProps: (values: IRelayReceiverStateProps<IActionStateProps>['values']) => TStateProps,
  children: IRenderProps<TStateProps>['children'],
  onStatePropsUpdated?: (stateProps: TStateProps) => void
): JSX.Element {
  const actionProps: IActionPropsByKey = {};
  const values: IRelayReceiverProps<IActionBaseProps>['values'] = {};

  for (const actionKey in actions) {
    if (Object.prototype.hasOwnProperty.call(actions, actionKey)) {
      const element = renderAction(actions[actionKey]);

      if (element) {
        if (element.type === ActionBase) {
          actionProps[actionKey] = element.props as IActionBaseProps;
        } else {
          values[actionKey] = (
            <BackgroundTaskContextProvider scope='local'>
              { element }
            </BackgroundTaskContextProvider>
          );
        }
      } else {
        actionProps[actionKey] = EMPTY_ACTION_PROPS;
      }
    }
  }

  return (
    <ACTION_RELAY.Receiver
      values={ values }
    >
      { withActionStateTracker(actionProps, (relayProps: IRelayReceiverStateProps<IActionStateProps>) => {
        const stateProps = getStateProps(relayProps.values);

        return (
          <>
            { renderChildrenWithProps(children, stateProps) }
            { onStatePropsUpdated ? (
              <StatePropsUpdater
                stateProps={ stateProps }
                onUpdate={ onStatePropsUpdated }
              />
            ) : null }
          </>
        );
      }) }
    </ACTION_RELAY.Receiver>
  );

}

function increment(key: string, value: number): (previousState: IActionExecutionTrackerState) => IActionExecutionTrackerState {
  return (previousState: IActionExecutionTrackerState) => {
    return graft(previousState, {
      actions: {
        [key]: {
          executingCount: new IncrementPatch(value)
        }
      }
    });
  };
}

async function defaultExecute(): Promise<void> {
  // No-op.
}
